import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Content } from '@/types/content';
import VideoPreviewCard from '@/components/video/VideoPreviewCard';
interface FeaturedVideosRowProps {
  featuredVideos: Content[];
  showViewAll?: boolean;
}
const VideoFeaturedSection: React.FC<FeaturedVideosRowProps> = ({
  featuredVideos,
  showViewAll = false
}) => {
  const navigate = useNavigate();
  const {
    resolvedTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';

  // Use the first three videos
  const mainVideo = featuredVideos[0] || null;
  const secondaryVideo = featuredVideos[1] || null;
  const tertiaryVideo = featuredVideos[2] || null;
  if (featuredVideos.length === 0) {
    return null;
  }

  // Handle navigation to video page
  const handleVideoClick = (video: Content) => {
    navigate(`/videos/${video.slug || video.id}`);
  };
  return <div className="mx-0">
      <div className="mb-8">
      {/* Desktop Layout */}
      <div className="hidden md:block">
        {/* Main featured video */}
        {mainVideo && <div className="mb-6">
            <div className="cursor-pointer" onClick={() => handleVideoClick(mainVideo)}>
              <VideoPreviewCard video={mainVideo} aspectRatio="16/9" className="mb-3" />
              <div>
                <h3 className={cn("text-xl font-chroma", textColor)}>
                  {mainVideo.title}
                </h3>
                <p className={cn("mt-1", mutedTextColor)}>
                  {mainVideo.description?.substring(0, 120)}
                  {mainVideo.description?.length > 120 ? '...' : ''}
                </p>
              </div>
            </div>
          </div>}
        
        {/* Two smaller videos in a row */}
        <div className="grid grid-cols-2 gap-6">
          {/* Secondary video */}
          {secondaryVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(secondaryVideo)}>
              <VideoPreviewCard video={secondaryVideo} aspectRatio="16/9" className="mb-2" />
              <h3 className={cn("text-base font-chroma", textColor)}>
                {secondaryVideo.title}
              </h3>
            </div>}
          
          {/* Tertiary video */}
          {tertiaryVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(tertiaryVideo)}>
              <VideoPreviewCard video={tertiaryVideo} aspectRatio="16/9" className="mb-2" />
              <h3 className={cn("text-base font-chroma", textColor)}>
                {tertiaryVideo.title}
              </h3>
            </div>}
        </div>
      </div>
      
      {/* Mobile Layout */}
      <div className={cn("md:hidden space-y-6")}>
        {/* Main featured video */}
        {mainVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(mainVideo)}>
            <VideoPreviewCard video={mainVideo} aspectRatio="16/9" className="mb-3" previewDelay={1200} />
            <h3 className={cn("text-lg font-chroma", textColor)}>
              {mainVideo.title}
            </h3>
            <p className={cn("mt-1 text-sm", mutedTextColor)}>
              {mainVideo.description?.substring(0, 80)}
              {mainVideo.description?.length > 80 ? '...' : ''}
            </p>
          </div>}
        
        {/* Two smaller videos in a row */}
        <div className="grid grid-cols-2 gap-3">
          {/* Secondary video */}
          {secondaryVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(secondaryVideo)}>
              <VideoPreviewCard video={secondaryVideo} aspectRatio="16/9" className="mb-2" previewDelay={1200} />
              <h3 className={cn("text-xs font-chroma", textColor)}>
                {secondaryVideo.title}
              </h3>
            </div>}
          
          {/* Tertiary video */}
          {tertiaryVideo && <div className="cursor-pointer" onClick={() => handleVideoClick(tertiaryVideo)}>
              <VideoPreviewCard video={tertiaryVideo} aspectRatio="16/9" className="mb-2" previewDelay={1200} />
              <h3 className={cn("text-xs font-chroma", textColor)}>
                {tertiaryVideo.title}
              </h3>
            </div>}
        </div>
      </div>
      </div>
    </div>;
};
export default VideoFeaturedSection;